import React from 'react';
import { useBatteryStore } from '../../store/batteryStore';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { 
  FileText, 
  Gauge, 
  Package, 
  Shield, 
  Check, 
  X, 
  AlertTriangle,
  Download,
  Copy
} from 'lucide-react';

const SpecsPanel = () => {
  const { selectedResult, cells, selectedCellId } = useBatteryStore();
  const cell = cells.find(c => c.id === selectedCellId);
  
  if (!selectedResult) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
        Select a pack configuration to view specifications
      </div>
    );
  }
  
  const specs = selectedResult.specs;
  const dims = selectedResult.layout.dimensions;
  
  const SpecRow = ({ label, value, unit, highlight }) => (
    <div className="flex justify-between py-1.5 border-b border-zinc-800/50">
      <span className="text-zinc-500 text-xs uppercase tracking-wider">{label}</span>
      <span className={`font-mono ${highlight ? 'text-cyan-400' : 'text-zinc-100'}`}>
        {value}
        {unit && <span className="text-zinc-500 ml-1 text-xs">{unit}</span>}
      </span>
    </div>
  );
  
  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        {/* Voltage Specs */}
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Voltage
          </h3>
          <div className="space-y-1">
            <SpecRow label="Configuration" value={`${specs.series}S${specs.parallel}P`} highlight />
            <SpecRow label="Nominal" value={specs.nominal_voltage?.toFixed(2)} unit="V" />
            <SpecRow label="Max (Charged)" value={specs.max_voltage?.toFixed(2)} unit="V" />
            <SpecRow label="Min (Cutoff)" value={specs.min_voltage?.toFixed(2)} unit="V" />
            {specs.voltage_loaded_cont && (
              <SpecRow label="Under Load" value={specs.voltage_loaded_cont?.toFixed(2)} unit="V" />
            )}
          </div>
        </div>
        
        <Separator className="bg-zinc-800" />
        
        {/* Capacity & Energy */}
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Capacity & Energy
          </h3>
          <div className="space-y-1">
            <SpecRow label="Capacity" value={specs.capacity_ah?.toFixed(2)} unit="Ah" />
            <SpecRow label="Capacity" value={specs.capacity_mah?.toFixed(0)} unit="mAh" />
            <SpecRow label="Energy (Nominal)" value={specs.capacity_wh?.toFixed(1)} unit="Wh" />
            {specs.capacity_wh_max && (
              <SpecRow label="Energy (Max)" value={specs.capacity_wh_max?.toFixed(1)} unit="Wh" />
            )}
            {specs.capacity_kwh && (
              <SpecRow label="Energy" value={specs.capacity_kwh?.toFixed(3)} unit="kWh" />
            )}
          </div>
        </div>
        
        <Separator className="bg-zinc-800" />
        
        {/* Current & Power */}
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Current & Power
          </h3>
          <div className="space-y-1">
            <SpecRow label="Continuous Current" value={specs.max_continuous_current?.toFixed(1)} unit="A" />
            <SpecRow label="Peak Current" value={specs.max_peak_current?.toFixed(1)} unit="A" />
            {specs.continuous_power_w && (
              <SpecRow label="Continuous Power" value={specs.continuous_power_w?.toFixed(0)} unit="W" />
            )}
            {specs.peak_power_w && (
              <SpecRow label="Peak Power" value={specs.peak_power_w?.toFixed(0)} unit="W" />
            )}
          </div>
        </div>
        
        <Separator className="bg-zinc-800" />
        
        {/* BMS Info (if included) */}
        {specs.bms_included && (
          <>
            <div>
              <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
                BMS (Battery Management System)
              </h3>
              <div className="space-y-1">
                <SpecRow label="BMS Model" value={specs.bms_name || 'Standard'} highlight />
                {specs.bms_max_continuous_current && (
                  <SpecRow label="BMS Max Continuous" value={specs.bms_max_continuous_current?.toFixed(1)} unit="A" />
                )}
                {specs.bms_max_peak_current && (
                  <SpecRow label="BMS Max Peak" value={specs.bms_max_peak_current?.toFixed(1)} unit="A" />
                )}
                {specs.bms_limited_continuous_current && (
                  <SpecRow 
                    label="Effective Continuous" 
                    value={specs.bms_limited_continuous_current?.toFixed(1)} 
                    unit="A" 
                    highlight 
                  />
                )}
                {specs.bms_limited_peak_current && (
                  <SpecRow 
                    label="Effective Peak" 
                    value={specs.bms_limited_peak_current?.toFixed(1)} 
                    unit="A" 
                    highlight 
                  />
                )}
                {specs.bms_dimensions_mm && (
                  <SpecRow 
                    label="BMS Dimensions" 
                    value={`${specs.bms_dimensions_mm.length}×${specs.bms_dimensions_mm.width}×${specs.bms_dimensions_mm.height}`} 
                    unit="mm" 
                  />
                )}
              </div>
            </div>
            <Separator className="bg-zinc-800" />
          </>
        )}
        
        {/* Resistance & Efficiency */}
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Resistance & Losses
          </h3>
          <div className="space-y-1">
            {specs.pack_resistance_mohm && (
              <SpecRow label="Pack Resistance" value={specs.pack_resistance_mohm?.toFixed(2)} unit="mΩ" />
            )}
            {specs.cell_resistance_mohm && (
              <SpecRow label="Cell Resistance" value={specs.cell_resistance_mohm?.toFixed(2)} unit="mΩ" />
            )}
            {specs.voltage_sag_cont && (
              <SpecRow label="Voltage Sag (Cont.)" value={specs.voltage_sag_cont?.toFixed(3)} unit="V" />
            )}
            {specs.power_loss_cont_w && (
              <SpecRow label="Power Loss (Cont.)" value={specs.power_loss_cont_w?.toFixed(1)} unit="W" />
            )}
          </div>
        </div>
        
        <Separator className="bg-zinc-800" />
        
        {/* Physical Specs */}
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Physical
          </h3>
          <div className="space-y-1">
            <SpecRow label="Cell Model" value={cell?.name || selectedCellId} />
            <SpecRow label="Total Cells" value={specs.total_cells} unit="pcs" />
            <SpecRow label="Weight" value={specs.weight_kg?.toFixed(2)} unit="kg" />
            <SpecRow label="Length" value={dims?.length?.toFixed(1)} unit="mm" />
            <SpecRow label="Width" value={dims?.width?.toFixed(1)} unit="mm" />
            <SpecRow label="Height" value={dims?.height?.toFixed(1)} unit="mm" />
          </div>
        </div>
        
        <Separator className="bg-zinc-800" />
        
        {/* Derived Metrics */}
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Performance Metrics
          </h3>
          <div className="space-y-1">
            {specs.energy_density_wh_kg && (
              <SpecRow label="Energy Density" value={specs.energy_density_wh_kg?.toFixed(1)} unit="Wh/kg" />
            )}
            {specs.specific_power_w_kg && (
              <SpecRow label="Specific Power" value={specs.specific_power_w_kg?.toFixed(1)} unit="W/kg" />
            )}
            <SpecRow 
              label="C-Rate (Cont.)" 
              value={(specs.max_continuous_current / specs.capacity_ah)?.toFixed(2)} 
              unit="C" 
            />
            {dims && (
              <SpecRow 
                label="Volume" 
                value={((dims.length * dims.width * dims.height) / 1000000)?.toFixed(3)} 
                unit="L" 
              />
            )}
          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

const BOMPanel = () => {
  const { selectedResult, exportCSV } = useBatteryStore();
  
  if (!selectedResult) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
        Select a pack configuration to view BOM
      </div>
    );
  }
  
  const bom = selectedResult.bom;
  
  const handleExportCSV = () => {
    const csv = exportCSV();
    if (csv) {
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `battery_pack_bom_${selectedResult.config.series}S${selectedResult.config.parallel}P.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };
  
  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-zinc-800/50 sticky top-0">
            <tr className="font-heading uppercase tracking-wider text-zinc-400">
              <th className="text-left p-2">Component</th>
              <th className="text-right p-2">Qty</th>
              <th className="text-left p-2">Unit</th>
              <th className="text-left p-2">SKU</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {bom.map((item, index) => (
              <tr key={index} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                <td className="p-2">
                  <div className="text-zinc-100">{item.component}</div>
                  <div className="text-zinc-500 text-[10px]">{item.description}</div>
                </td>
                <td className="p-2 text-right text-cyan-400">{item.quantity}</td>
                <td className="p-2 text-zinc-500">{item.unit}</td>
                <td className="p-2 text-zinc-500">{item.sku || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="p-3 border-t border-zinc-800 flex gap-2">
        <Button 
          size="sm" 
          variant="outline" 
          onClick={handleExportCSV}
          className="flex-1"
          data-testid="export-csv-btn"
        >
          <Download className="w-3 h-3 mr-1" />
          Export CSV
        </Button>
      </div>
    </div>
  );
};

const ChecksheetPanel = () => {
  const { selectedResult, config, cells, selectedCellId } = useBatteryStore();
  const cell = cells.find(c => c.id === selectedCellId);
  
  if (!selectedResult) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
        Select a pack configuration to view checksheet
      </div>
    );
  }
  
  const specs = selectedResult.specs;
  const validation = selectedResult.validation;
  
  const CheckItem = ({ label, checked, value }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-zinc-800/50">
      <span className="text-zinc-400 text-xs">{label}</span>
      <div className="flex items-center gap-2">
        {value && <span className="font-mono text-zinc-300 text-xs">{value}</span>}
        {checked ? (
          <Check className="w-4 h-4 text-green-500" />
        ) : (
          <X className="w-4 h-4 text-red-500" />
        )}
      </div>
    </div>
  );
  
  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Configuration Check
          </h3>
          <div className="space-y-1">
            <CheckItem label="Cell Model Selected" checked={!!cell} value={cell?.name} />
            <CheckItem 
              label="Series Configuration" 
              checked={selectedResult.config.series > 0} 
              value={`${selectedResult.config.series}S`} 
            />
            <CheckItem 
              label="Parallel Configuration" 
              checked={selectedResult.config.parallel > 0} 
              value={`${selectedResult.config.parallel}P`} 
            />
            <CheckItem 
              label="Layers Defined" 
              checked={selectedResult.config.layers > 0} 
              value={`${selectedResult.config.layers}`} 
            />
          </div>
        </div>
        
        <Separator className="bg-zinc-800" />
        
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Electrical Validation
          </h3>
          <div className="space-y-1">
            <CheckItem 
              label="Nominal Voltage" 
              checked={specs.nominal_voltage > 0} 
              value={`${specs.nominal_voltage?.toFixed(2)}V`}
            />
            <CheckItem 
              label="Max Voltage < 100V" 
              checked={specs.max_voltage < 100} 
              value={`${specs.max_voltage?.toFixed(2)}V`}
            />
            <CheckItem 
              label="Capacity Calculated" 
              checked={specs.capacity_ah > 0} 
              value={`${specs.capacity_ah?.toFixed(2)}Ah`}
            />
            <CheckItem 
              label="Energy Calculated" 
              checked={specs.capacity_wh > 0} 
              value={`${specs.capacity_wh?.toFixed(1)}Wh`}
            />
            {specs.pack_resistance_mohm && (
              <CheckItem 
                label="Pack Resistance" 
                checked={specs.pack_resistance_mohm < 200} 
                value={`${specs.pack_resistance_mohm?.toFixed(2)}mΩ`}
              />
            )}
          </div>
        </div>
        
        <Separator className="bg-zinc-800" />
        
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Validation Status
          </h3>
          <div className="space-y-1">
            <CheckItem 
              label="S/P Requirement Met" 
              checked={validation.meets_sp_requirement} 
            />
            <CheckItem 
              label="All Cells Connected" 
              checked={validation.all_cells_connected} 
            />
            <CheckItem 
              label="Blocks Balanced" 
              checked={validation.blocks_balanced} 
            />
            <CheckItem 
              label="No Overlapping" 
              checked={validation.no_overlapping} 
            />
            <CheckItem 
              label="Within Dimensions" 
              checked={validation.within_dimensions} 
            />
            {validation.voltage_sag_percent !== undefined && (
              <CheckItem 
                label="Voltage Sag < 10%" 
                checked={validation.voltage_sag_percent < 10} 
                value={`${validation.voltage_sag_percent?.toFixed(2)}%`}
              />
            )}
            {validation.thermal_warning !== undefined && (
              <CheckItem 
                label="Thermal OK" 
                checked={!validation.thermal_warning} 
              />
            )}
          </div>
        </div>
        
        <Separator className="bg-zinc-800" />
        
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Pack Score
          </h3>
          <div className="bg-zinc-800/50 rounded p-3 text-center">
            <div className="text-3xl font-mono text-cyan-400">
              {selectedResult.score?.toFixed(1)}
            </div>
            <div className="text-xs text-zinc-500 mt-1">Ranking Score</div>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

const SafetyPanel = () => {
  const { selectedResult } = useBatteryStore();
  
  if (!selectedResult) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
        Select a pack configuration to view safety info
      </div>
    );
  }
  
  const validation = selectedResult.validation;
  const specs = selectedResult.specs;
  
  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        {/* Overall Status */}
        <div className={`p-4 rounded-lg ${
          validation.is_valid 
            ? 'bg-green-500/10 border border-green-500/30' 
            : 'bg-red-500/10 border border-red-500/30'
        }`}>
          <div className="flex items-center gap-2">
            {validation.is_valid ? (
              <>
                <Check className="w-5 h-5 text-green-500" />
                <span className="font-heading uppercase tracking-wider text-green-400">
                  Valid Configuration
                </span>
              </>
            ) : (
              <>
                <X className="w-5 h-5 text-red-500" />
                <span className="font-heading uppercase tracking-wider text-red-400">
                  Configuration Issues Found
                </span>
              </>
            )}
          </div>
        </div>
        
        {/* Electrical Safety Metrics */}
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3 flex items-center gap-2">
            <Gauge className="w-4 h-4" />
            Electrical Safety
          </h3>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between p-2 bg-zinc-800/30 rounded">
              <span className="text-zinc-400">Max Voltage</span>
              <span className={`font-mono ${specs.max_voltage > 60 ? 'text-yellow-400' : 'text-green-400'}`}>
                {specs.max_voltage?.toFixed(2)}V
                {specs.max_voltage > 60 && ' ⚠️'}
              </span>
            </div>
            {validation.voltage_sag_percent !== undefined && (
              <div className="flex justify-between p-2 bg-zinc-800/30 rounded">
                <span className="text-zinc-400">Voltage Sag</span>
                <span className={`font-mono ${validation.voltage_sag_percent > 10 ? 'text-yellow-400' : 'text-green-400'}`}>
                  {validation.voltage_sag_percent?.toFixed(2)}%
                </span>
              </div>
            )}
            {validation.power_loss_percent !== undefined && (
              <div className="flex justify-between p-2 bg-zinc-800/30 rounded">
                <span className="text-zinc-400">Power Loss</span>
                <span className={`font-mono ${validation.power_loss_percent > 5 ? 'text-yellow-400' : 'text-green-400'}`}>
                  {validation.power_loss_percent?.toFixed(2)}%
                </span>
              </div>
            )}
            {specs.power_loss_cont_w && (
              <div className="flex justify-between p-2 bg-zinc-800/30 rounded">
                <span className="text-zinc-400">Heat Dissipation</span>
                <span className={`font-mono ${specs.power_loss_cont_w > 50 ? 'text-yellow-400' : 'text-green-400'}`}>
                  {specs.power_loss_cont_w?.toFixed(1)}W
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Errors */}
        {validation.errors.length > 0 && (
          <div>
            <h3 className="font-heading text-sm uppercase tracking-wider text-red-400 mb-3 flex items-center gap-2">
              <X className="w-4 h-4" />
              Errors ({validation.errors.length})
            </h3>
            <div className="space-y-2">
              {validation.errors.map((error, i) => (
                <div 
                  key={i} 
                  className="bg-red-500/10 border border-red-500/20 rounded p-2 text-xs text-red-300"
                >
                  {error}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Warnings */}
        {validation.warnings.length > 0 && (
          <div>
            <h3 className="font-heading text-sm uppercase tracking-wider text-yellow-400 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Warnings ({validation.warnings.length})
            </h3>
            <div className="space-y-2">
              {validation.warnings.map((warning, i) => (
                <div 
                  key={i} 
                  className="bg-yellow-500/10 border border-yellow-500/20 rounded p-2 text-xs text-yellow-300"
                >
                  {warning}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Safety Checklist */}
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Safety Checklist
          </h3>
          <div className="space-y-2 text-xs">
            <div className="flex items-start gap-2 p-2 bg-zinc-800/30 rounded">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-zinc-300">Ensure proper insulation between all series groups</span>
            </div>
            <div className="flex items-start gap-2 p-2 bg-zinc-800/30 rounded">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-zinc-300">
                Use appropriate gauge wire for {specs.max_continuous_current?.toFixed(0)}A rated current
              </span>
            </div>
            <div className="flex items-start gap-2 p-2 bg-zinc-800/30 rounded">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-zinc-300">
                Install {specs.series}S BMS with ≥{specs.max_continuous_current?.toFixed(0)}A rating
              </span>
            </div>
            <div className="flex items-start gap-2 p-2 bg-zinc-800/30 rounded">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-zinc-300">Verify all spot welds before final assembly</span>
            </div>
            <div className="flex items-start gap-2 p-2 bg-zinc-800/30 rounded">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-zinc-300">
                Test pack voltage: expect {specs.nominal_voltage?.toFixed(1)}V nominal, max {specs.max_voltage?.toFixed(1)}V
              </span>
            </div>
            {specs.power_loss_cont_w > 20 && (
              <div className="flex items-start gap-2 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded">
                <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                <span className="text-yellow-300">
                  Pack dissipates {specs.power_loss_cont_w?.toFixed(1)}W at continuous load - ensure adequate cooling
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

export const TabPanels = () => {
  const { activeTab, setActiveTab, selectedResult, exportJSON } = useBatteryStore();
  
  const handleExportJSON = () => {
    const data = exportJSON();
    if (data) {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `battery_pack_config_${data.config.series}S${data.config.parallel}P.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };
  
  return (
    <div className="h-full flex flex-col bg-zinc-900/50 rounded-lg border border-zinc-800" data-testid="tab-panels">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <div className="flex items-center justify-between border-b border-zinc-800 px-2">
          <TabsList className="bg-transparent h-10">
            <TabsTrigger 
              value="specs" 
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-cyan-400 text-xs"
              data-testid="tab-specs"
            >
              <Gauge className="w-3 h-3 mr-1" />
              Specs
            </TabsTrigger>
            <TabsTrigger 
              value="bom" 
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-cyan-400 text-xs"
              data-testid="tab-bom"
            >
              <Package className="w-3 h-3 mr-1" />
              Parts List
            </TabsTrigger>
            <TabsTrigger 
              value="checksheet" 
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-cyan-400 text-xs"
              data-testid="tab-checksheet"
            >
              <FileText className="w-3 h-3 mr-1" />
              Checksheet
            </TabsTrigger>
            <TabsTrigger 
              value="safety" 
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-cyan-400 text-xs"
              data-testid="tab-safety"
            >
              <Shield className="w-3 h-3 mr-1" />
              Safety
            </TabsTrigger>
          </TabsList>
          
          {selectedResult && (
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={handleExportJSON}
              className="text-xs"
              data-testid="export-json-btn"
            >
              <Download className="w-3 h-3 mr-1" />
              Export JSON
            </Button>
          )}
        </div>
        
        <TabsContent value="specs" className="flex-1 m-0">
          <SpecsPanel />
        </TabsContent>
        <TabsContent value="bom" className="flex-1 m-0">
          <BOMPanel />
        </TabsContent>
        <TabsContent value="checksheet" className="flex-1 m-0">
          <ChecksheetPanel />
        </TabsContent>
        <TabsContent value="safety" className="flex-1 m-0">
          <SafetyPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};
